<template>
  <div class="notes-container">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">الهوامش والملاحظات</span>
        <v-btn
          v-if="hasPermission('admin.marginalizations.store')"
          color="primary"
          @click="openAddDialog"
        >
          <v-icon left>mdi-plus</v-icon>
          إضافة هامش جديد
        </v-btn>
      </v-card-title>
      
      <v-card-text>
        <!-- Search -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="البحث في الهوامش"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      
      <!-- Notes Table -->
      <v-data-table
        :headers="headers"
        :items="filteredNotes"
        :loading="loading"
        :items-per-page="perPage"
        :page="page"
        color="primary"
        first-icon="mdi-chevron-left"
        last-icon="mdi-chevron-right"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        show-current-page
        show-first-last-page
        show-first-page
        show-last-page
        show-next-page
        show-previous-page
        class="elevation-1"
      >
        <template #[`item.desc`]="{ item }">
          <div class="text-truncate" style="max-width: 300px;">
            {{ item.desc }}
          </div>
        </template>
        
        <template #[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        
        <template #[`item.is_private`]="{ item }">
          <v-chip
            :color="item.is_private ? 'primary' : 'info'"
            label
            size="small"
          >
            {{ item.is_private ? 'خاص' : 'عام' }}
          </v-chip>
        </template>
        
        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="hasPermission('admin.marginalizations.update')"
            icon
            variant="text"
            size="small"
            color="primary"
            @click="editNote(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="hasPermission('admin.marginalizations.destroy')"
            icon
            variant="text"
            size="small"
            color="error"
            @click="deleteNote(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    
    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span>{{ isEditing ? 'تعديل الهامش' : 'إضافة هامش جديد' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="editedItem.title"
              label="عنوان الهامش"
              :rules="noteRules.title"
              required
            ></v-text-field>
            
            <v-textarea
              v-model="editedItem.desc"
              label="وصف الهامش"
              :rules="noteRules.desc"
              rows="4"
              required
            ></v-textarea>
            
            <v-switch
              v-model="editedItem.is_private"
              label="هامش خاص"
              color="primary"
              hide-details
              class="mt-3"
            ></v-switch>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="save"
            :loading="loading"
          >
            حفظ
          </v-btn>
          <v-btn
            color="error"
            variant="text"
            @click="close"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف الهامش "{{ itemToDelete?.title }}"؟
          <br>
          <small class="text-grey">لا يمكن التراجع عن هذا الإجراء.</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="text"
            @click="confirmDelete"
          >
            حذف
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            @click="deleteDialog = false"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import marginalizationService from '@/services/marginalizationService'
import { validationRules } from '@/assets/validations'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'NotesView',
  data() {
    return {
      search: '',
      loading: false,
      dialog: false,
      valid: false,
      isEditing: false,
      deleteDialog: false,
      itemToDelete: null,
      
      // Pagination
      page: 1,
      perPage: 15,
      totalItems: 0,
      
      headers: [
        { title: 'العنوان', key: 'title', align: 'start' },
        { title: 'الوصف', key: 'desc', align: 'start' },
        { title: 'تاريخ الإنشاء', key: 'created_at', align: 'center' },
        { title: 'نوع الهامش', key: 'is_private', align: 'center' },
        { title: 'الإجراءات', key: 'actions', sortable: false, align: 'center' }
      ],
      notes: [],
      editedItem: {
        id: null,
        title: '',
        desc: '',
        is_private: false
      },
      defaultItem: {
        id: null,
        title: '',
        desc: '',
        is_private: false
      },
      
      // Validation rules
      noteRules: {
        title: [validationRules.required('عنوان الهامش')],
        desc: [validationRules.required('وصف الهامش')]
      }
    }
  },
  
  mounted() {
    this.fetchNotes()
  },
  
  computed: {
    filteredNotes() {
      let result = [...this.notes];
      
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        result = result.filter(note => 
          note.title?.toLowerCase().includes(searchLower) ||
          note.desc?.toLowerCase().includes(searchLower)
        );
      }
      
      return result;
    }
  },
  
  methods: {

    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },

    async fetchNotes() {
      try {
        this.loading = true
        const params = {
          page: this.page,
          per_page: this.perPage
        }
        
        const response = await marginalizationService.getMarginalizations(params)
        
        if (response.success) {
          this.notes = response.data.data || response.data
          this.totalItems = response.data.total || this.notes.length
          this.page = response.data.current_page || 1
          
          // Add is_private property based on department_id logic
          this.notes = this.notes.map(note => ({
            ...note,
            is_private: note.department_id !== null
          }))
        } else {
          this.$alerts.error('فشل في تحميل بيانات الهوامش')
        }
      } catch (error) {
        console.error('Error fetching notes:', error)
        this.$alerts.error('حدث خطأ أثناء تحميل بيانات الهوامش')
      } finally {
        this.loading = false
      }
    },
    
    openAddDialog() {
      this.isEditing = false;
      this.editedItem = { ...this.defaultItem };
      this.dialog = true;
    },
    
    editNote(item) {
      this.isEditing = true;
      this.editedItem = { 
        ...item,
        is_private: item.department_id !== null
      };
      this.dialog = true;
    },
    
    deleteNote(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      try {
        const response = await marginalizationService.deleteMarginalization(this.itemToDelete.id);
        if (response.success) {
          this.$alerts.success('تم حذف الهامش بنجاح');
          this.fetchNotes(); // Refresh the list
        } else {
          this.$alerts.error('فشل في حذف الهامش');
        }
        this.deleteDialog = false;
        this.itemToDelete = null;
      } catch (error) {
        console.error('Error deleting note:', error);
        this.$alerts.error('حدث خطأ أثناء حذف الهامش');
      }
    },
    
    async save() {
      if (!this.$refs.form.validate()) return;
      
      try {
        this.loading = true;
        
        if (this.isEditing) {
          const response = await marginalizationService.updateMarginalization(this.editedItem.id, {
            title: this.editedItem.title,
            desc: this.editedItem.desc,
            is_private: this.editedItem.is_private
          });
          if (response.success) {
            this.$alerts.success('تم تحديث الهامش بنجاح');
            this.fetchNotes(); // Refresh the list
          } else {
            this.$alerts.error('فشل في تحديث الهامش');
          }
        } else {
          const response = await marginalizationService.createMarginalization({
            title: this.editedItem.title,
            desc: this.editedItem.desc,
            is_private: this.editedItem.is_private
          });
          if (response.success) {
            this.$alerts.success('تم إضافة الهامش بنجاح');
            this.fetchNotes(); // Refresh the list
          } else {
            this.$alerts.error('فشل في إضافة الهامش');
          }
        }
        
        this.close();
      } catch (error) {
        console.error('Error saving note:', error);
        this.$alerts.error('حدث خطأ أثناء حفظ بيانات الهامش');
      } finally {
        this.loading = false;
      }
    },
    
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.$refs.form?.reset();
      });
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('ar-LY');
    }
  }
}
</script>

<style scoped>
.notes-container {
  padding: 16px;
}

:deep(.v-data-table) {
  background-color: white;
}

:deep(.v-data-table-header) {
  background-color: #f5f5f5;
}
</style> 